import { DependencyList, useCallback, useMemo } from "react";

import { FlagWithSubRows } from "@/feature/data/useFlags";

export function useFlagsSearch<T extends FlagWithSubRows>(
  flags: T[],
  options: {
    enabled: boolean;
    predicate: (flag: T) => boolean;
  },
  deps: DependencyList | undefined = [],
) {
  const isChildMatch = useCallback(
    (f: T): boolean => {
      return (
        hasSubRows(f) &&
        f.subRows.some((r) => {
          return options.predicate(r as T) || isChildMatch(r as T);
        })
      );
    },
    [options],
  );

  return useMemo(
    () =>
      !options.enabled
        ? flags
        : flags
            .filter((flag) => options.predicate(flag) || isChildMatch(flag))
            .map((flag) => ({
              ...flag,
              subRows: hasSubRows(flag)
                ? flag.subRows.filter(
                    (s) => options.predicate(s as T) || isChildMatch(s as T),
                  )
                : undefined,
            })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps are external
    [flags, isChildMatch, options, ...deps],
  );
}

type FlagWithSubRowsRequired<T extends FlagWithSubRows> = T & {
  subRows: T[];
};

function hasSubRows<T extends FlagWithSubRows>(
  f: T,
): f is FlagWithSubRowsRequired<T> {
  return "subRows" in f && Array.isArray(f.subRows);
}
